import dayjs from 'dayjs';
import { CoachOnboardingRow } from '../../types/opsBriefing';

/**
 * 강사별 온보딩 카드 — **화면이 쓰는 값을 만드는 한 곳** (2026-09-01).
 *
 * ─────────────────────────────────────────────────────────────
 * 정본
 * ─────────────────────────────────────────────────────────────
 * · 시안   `OPS_BRIEFING_ONBOARDING_UI.html` — 셀 여섯 칸·문구·색이 여기 기준
 * · 정책   `OPS_BRIEFING_ONBOARDING_POLICY (2).md` — 등장·소멸·반복 규칙
 * · 계약   `CoachOnboardingRow` (types/opsBriefing.ts)
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 이 파일이 지는 책임
 * ─────────────────────────────────────────────────────────────
 * 카드가 **무엇을 말하는가**(헤드라인·잠금·색·순서)는 전부 여기서 정한다.
 * 화면(`CoachOnboardingCard`)은 여기서 나온 것을 그리기만 한다.
 *
 * 이렇게 가르는 이유는 같은 판정을 **세 곳이 쓰기 때문**이다 —
 * 오늘의 브리핑 화면 · 아침 팝업 · 「오늘 챙길 일 N건」 카운터.
 * 셋이 각자 세면 팝업과 화면이 다른 수를 말한다(QA34 에서 실제로 그랬다).
 *
 * ⚠️ **0 과 null 은 다르다** (정책 P3 · C8). null 은 「못 쟀다」이지
 *    「없다」가 아니다. 이 파일은 null 을 절대 0 으로 접지 않는다 —
 *    분자가 null 이면 그 셀은 「확인 중」이고 판정에서도 빠진다.
 */

/** 퍼널 단계 — 순서가 곧 우선순위다 (정책 §3-1) */
export type CoachStage =
    /** 센터 연결 수락 전 */
    | 'ACCEPT'
    /** 수락은 했는데 담당 회원이 0명 — 배정할 사람이 실제로 없는 상태 */
    | 'ASSIGN'
    /** 담당 회원 중 활성 수강권 없음 */
    | 'PASS'
    /** 수강권은 있는데 향후 예약 없음 */
    | 'BOOKING'
    /** 최근 7일 완료 수업 중 일지 없음 */
    | 'JOURNAL'
    /** 위 전부 해당 없음 — 순항 (챙길 일에 안 셈) */
    | 'CLEAR';

/**
 * 퍼널 순서. **배열 순서가 판정 순서다** — 첫 번째로 걸리는 것이 헤드라인이 된다.
 *
 * ⚠️ 「회원 배정」은 단계가 아니다 (대표 결정 — 앱 연결 때 자연히 된다).
 *    담당 0명 + 수락 완료일 때만 예외로 낸다. 그래서 ACCEPT 바로 뒤다.
 */
const FUNNEL: CoachStage[] = ['ACCEPT', 'ASSIGN', 'PASS', 'BOOKING', 'JOURNAL'];

/** 에스컬레이션 단계 (정책 §4-2 · 대표 확정 D2 = 3 / 7 / 14 / 15+) */
export type EscalationTier = 1 | 2 | 3 | 4;

/** 단계별 경과일 하한 — `[tier2, tier3, tier4]` */
const TIER_DAYS = [4, 8, 15] as const;

/**
 * 처리 체크 후 **조용한 모드**로 두는 기간 (정책 §4-3 · 대표 확정 D3).
 *
 * 연락했는데 매일 독촉하면 원장님은 자기 잘못이 아닌 일로 시달린다.
 */
export const QUIET_DAYS = 3;

/**
 * 경과일 → 에스컬레이션 단계.
 *
 * 🔴 **서버가 `tier` 를 주면 그것이 이긴다.** 지금 계약에는 없는 필드라
 *    화면이 세지만, 서버가 실어 보내기 시작하면 그날로 넘어간다 —
 *    두 곳이 각자 세면 문구가 갈린다.
 */
export const escalationOf = (row: CoachOnboardingRow, days: number | null): EscalationTier => {
    if (row.tier) return row.tier;
    if (days == null) return 1;
    if (days >= TIER_DAYS[2]) return 4;
    if (days >= TIER_DAYS[1]) return 3;
    if (days >= TIER_DAYS[0]) return 2;
    return 1;
};

/**
 * 수락 대기 경과일 — 등록일부터 오늘까지.
 *
 * ⚠️ 수락한 강사에게는 **의미가 없다.** null 을 돌려준다 — 0 이 아니다.
 */
export const waitingDaysOf = (row: CoachOnboardingRow, today: dayjs.Dayjs): number | null => {
    if (row.isConnected) return null;
    const from = dayjs(row.registeredOn);
    if (!from.isValid()) return null;
    /*
     * 🔴 **경과일은 등록일로부터의 차이 그대로다** — 시안이 기준이다.
     *    8/20 등록 · 9/1 아침 = 「12일째」 (시안 김수산나 카드).
     *    여기에 +1 을 하면 13일째가 되어 시안·검산표와 어긋난다.
     *
     * 하한만 1 로 둔다 — 「0일째 수락하지 않았습니다」는 말이 안 되고,
     * 등록 당일에 이 카드가 뜨면 그건 1일째다.
     */
    return Math.max(1, today.startOf('day').diff(from.startOf('day'), 'day'));
};

/**
 * ─────────────────────────────────────────────────────────────
 * 🔴 아주 오래된 미수락은 **오늘 할 일이 아니다** (2026-09-02 QA38)
 * ─────────────────────────────────────────────────────────────
 * 실화면에서 「785일째 센터 연결을 수락하지 않았습니다」가 앞줄에 떴다.
 * 2024-07 등록 · 담당 회원 0명 — 2년 넘게 그대로인 유령 데이터다.
 *
 * 계산도 문구도 틀리지 않았다. 문제는 **자리**다.
 *   · `sortCards` 가 같은 단계에서 「오래 기다린 쪽이 위」라, 이 유령이
 *     3일 전 가입한 진짜 신규 강사보다 **항상 앞**에 선다.
 *   · 앞줄은 최대 두 장이라 사실상 한 자리를 영구 점유하고,
 *     「오늘 챙길 일」 건수도 매일 +1 한다.
 *   · 담당 0명이면 수락해도 뒤 축(수강권·예약·일지)이 전부 비어 있어
 *     실질 효과도 없다.
 *
 * ⚠️ **임의값이다. 운영해 보고 조정한다.** 분기(90일)가 지나도 수락하지
 *    않았으면 그 강사는 「오늘 연락해서 될 일」이 아니라고 본다.
 */
export const STALE_ACCEPT_DAYS = 90;

/**
 * 분기가 지나도록 센터 연결을 수락하지 않은 강사인가.
 *
 * 🔴 **화면에서 지우지는 않는다.** 「오늘 챙길 일」에서만 뺀다(`chore`).
 *    지워 버리면 원장님이 그런 강사가 있다는 것조차 모르게 되고,
 *    정리하려 해도 찾을 데가 없다. 강사별 온보딩 화면에는 순항 카드와
 *    같은 자리(맨 아래)에 남는다.
 */
export const isStaleAccept = (row: CoachOnboardingRow, today: dayjs.Dayjs): boolean => {
    const days = waitingDaysOf(row, today);
    return days != null && days >= STALE_ACCEPT_DAYS;
};

/** 미해소 수 — 분자가 null 이면 **못 잰 것**이라 0 이 아니라 null 이다 */
const shortfall = (total: number, done: number | null): number | null =>
    done == null ? null : Math.max(0, total - done);

/**
 * **예약이 가능한 모집단** — 지금 유효한 수강권을 가진 회원 수 (2026-09-02 QA6).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 `memberWithPass` 를 여기 쓰면 안 된다
 * ─────────────────────────────────────────────────────────────
 * QA6 로 `memberWithPass` 의 뜻이 「지금 활성」에서 **「한 번이라도 등록한
 * 적 있음」**(만료자 포함)으로 바뀌었다. 만료자는 예약을 할 수 없으므로,
 * 그 값을 예약 분모로 쓰면 **영원히 「예약 미완료」로 남는다** — 원장님은
 * 해소할 방법이 없는 항목을 매일 본다.
 *
 * ⚠️ 구버전 서버에는 이 필드가 없다. 그때는 `memberWithPass` 가 곧 활성
 *    수였으므로 그대로 폴백한다 — 기능 감지다.
 */
const activePassOf = (row: CoachOnboardingRow): number | null =>
    row.memberWithActivePass ?? row.memberWithPass;

/**
 * 이 강사의 **첫 번째 막힌 단계**.
 *
 * 정책 §3-1 의 표를 그대로 옮긴 것이고, 순서는 `FUNNEL` 이 정한다.
 * 못 잰 값(null)은 **막힌 것으로 치지 않는다** — 모르는 것으로 독촉할 수 없다.
 */
export const stageOf = (row: CoachOnboardingRow): CoachStage => {
    const noPass = shortfall(row.memberCount, row.memberWithPass);
    /* 🔴 예약은 **활성** 분모다 (activePassOf 주석) */
    const activePass = activePassOf(row);
    const noBooking = activePass == null ? null : shortfall(activePass, row.memberWithBooking);
    const noJournal = row.journal7d ? Math.max(0, row.journal7d.total - row.journal7d.written) : null;

    const blocked: Record<CoachStage, boolean> = {
        ACCEPT: !row.isConnected,
        ASSIGN: row.isConnected && row.memberCount === 0,
        PASS: (noPass ?? 0) > 0,
        BOOKING: (noBooking ?? 0) > 0,
        JOURNAL: (noJournal ?? 0) > 0,
        CLEAR: false,
    };
    return FUNNEL.find((s) => blocked[s]) ?? 'CLEAR';
};

/** 카드가 「오늘 챙길 일」에 세어지나 — 순항은 안 센다 (정책 §2·§3-2) */
export const isChore = (row: CoachOnboardingRow): boolean => stageOf(row) !== 'CLEAR';

/**
 * 헤드라인 — 시안의 `.head` 한 줄.
 *
 * 🔴 **문구가 매일 달라야 한다** (정책 P2). 경과일이 들어가는 문장은 그 자체로
 *    매일 바뀌고, 수락 카드는 경과에 따라 **문장 자체가 교체**된다(§4-2).
 */
export const headlineOf = (
    row: CoachOnboardingRow,
    stage: CoachStage,
    days: number | null,
    tier: EscalationTier
): string => {
    const noPass = shortfall(row.memberCount, row.memberWithPass) ?? 0;
    const activePass = activePassOf(row);
    const noBooking = activePass == null ? 0 : (shortfall(activePass, row.memberWithBooking) ?? 0);
    const noJournal = row.journal7d ? Math.max(0, row.journal7d.total - row.journal7d.written) : 0;

    switch (stage) {
        case 'ACCEPT': {
            const head = days == null ? '센터 연결을 수락하지 않았습니다' : `${days}일째 센터 연결을 수락하지 않았습니다`;
            /*
             * 에스컬레이션 (§4-2). 같은 독촉을 매일 반복하지 않는다.
             *
             * ⚠️ 4단(15일+)은 대표 확정 D4 로 넣되 **문구를 순화**한다.
             *    「이 강사를 계속 두시겠어요?」는 원장이 강사에게 그대로 보여줄
             *    수도 있는 화면에서 쓸 말이 아니다 — 사람을 지우는 문장이 된다.
             *    사실만 적고 판단은 원장에게 남긴다.
             */
            if (tier >= 4) return `${head} — 등록 정보를 다시 확인해 보세요`;
            if (tier === 3) return `${head} — 문자로도 안 되면 전화가 빠릅니다`;
            return head;
        }
        case 'ASSIGN':
            return '담당할 회원을 배정해 주세요';
        case 'PASS':
            return `담당 회원 ${noPass}명에게 수강권이 없습니다`;
        case 'BOOKING':
            return `수강권은 끝났습니다 — 회원 ${noBooking}명의 수업 예약이 없습니다`;
        case 'JOURNAL':
            return `일지 없는 수업이 ${noJournal}건입니다`;
        default:
            return '';
    }
};

/**
 * 🔓 해금 안내 — 「이걸 하면 무엇이 열리나」.
 *
 * 시안의 초록 줄이다. 세팅은 그 자체로는 보람이 없는 일이라, **무엇이
 * 시작되는지**를 붙여야 원장님이 움직인다.
 */
export const unlockNoteOf = (stage: CoachStage): string | null => {
    switch (stage) {
        case 'ACCEPT':
            return '수락 한 번이면 — 이 강사의 수업 예약 · 일지가 바로 열립니다';
        case 'ASSIGN':
            return '배정하면 — 그 회원의 수강권 · 예약 관리가 이 강사에게 열립니다';
        case 'PASS':
            return '등록하면 — 잔여 · 만료 · 재등록 알림이 자동으로 시작됩니다';
        case 'BOOKING':
            return '예약이 잡히면 — 출석 · 페이스 분석이 자동으로 시작됩니다';
        case 'JOURNAL':
            return '일지가 쌓이면 — 회원별 변화 추적이 시작됩니다';
        default:
            return null;
    }
};

/**
 * 우측 배지 — 「수강권 없음 3명」.
 *
 * 🔴 **「회원 N명이 기다립니다」는 넣지 않는다** (대표 확정 ⓐ).
 *    시안에는 수락 카드에 그 배지가 있었지만, 「기다린다」는 상태가 데이터에
 *    없다. 수락 전 강사에게 배정된 회원이 그를 기다리는 중인지 아닌지를
 *    이 제품은 모른다 — 모르는 것을 단정하면 원장이 그 말을 강사에게 옮긴다.
 */
export const badgeOf = (row: CoachOnboardingRow, stage: CoachStage): string | null => {
    const noPass = shortfall(row.memberCount, row.memberWithPass);
    const activePass = activePassOf(row);
    const noBooking = activePass == null ? null : shortfall(activePass, row.memberWithBooking);
    const noJournal = row.journal7d ? Math.max(0, row.journal7d.total - row.journal7d.written) : null;

    if (stage === 'PASS' && noPass) return `수강권 없음 ${noPass}명`;
    if (stage === 'BOOKING' && noBooking) return `예약 없는 회원 ${noBooking}명`;
    if (stage === 'JOURNAL' && noJournal) return `일지 없는 수업 ${noJournal}건`;
    return null;
};

/** 카드 머리의 성격 태그 — 누가 움직여야 하나 */
export const actorOf = (stage: CoachStage): { label: string; tone: 'coach' | 'owner' | 'clear' } => {
    if (stage === 'CLEAR') return { label: '순항 중', tone: 'clear' };
    /* 수락은 강사가 앱에서 눌러야 한다 — 원장이 대신 못 한다 */
    if (stage === 'ACCEPT') return { label: '강사가 할 일', tone: 'coach' };
    return { label: '원장님이 할 일', tone: 'owner' };
};

/* ── 셀 여섯 칸 ──────────────────────────────────────────── */

export type CellTone = 'plain' | 'good' | 'warn' | 'hot' | 'off';

export type OnboardingCell = {
    /** 칸 이름 */
    k: string;
    /** 큰 값. 「확인 중」·「수락 후」도 여기 들어간다 */
    v: string;
    /** 값 뒤에 붙는 작은 단위 — 「/3명」·「건」 */
    unit?: string;
    /** 아래 작은 줄 — 「12일째」·「없음 2」 */
    s?: string;
    /** 마우스오버로 밝히는 뜻 — 라벨만으로 오해될 수 있는 칸에 붙인다 */
    title?: string;
    tone: CellTone;
};

/** 못 잰 값 — 0 으로 접지 않는다 (P3) */
const UNKNOWN: Pick<OnboardingCell, 'v' | 'tone'> = { v: '확인 중', tone: 'off' };

/**
 * 카드의 여섯 칸.
 *
 * ⚠️ **잠금은 「수락」에만 걸린다.** 시안에서 수강권이 막힌 강사(손수민)도
 *    「이번 주 수업 2건」이 그대로 보인다 — 수강권이 없으면 예약이 불가할 뿐,
 *    이미 있는 수업까지 가릴 이유가 없다. 잠그는 것은 아직 **관계가 없는**
 *    강사(미수락)뿐이다.
 */
export const cellsOf = (row: CoachOnboardingRow, stage: CoachStage): OnboardingCell[] => {
    const locked = !row.isConnected;
    const lock = (k: string): OnboardingCell => ({ k, v: '수락 후', tone: 'off' });

    const accept: OnboardingCell = row.isConnected
        ? {
              k: '센터 수락',
              v: '✓',
              s: row.connectedOn ? dayjs(row.connectedOn).format('M/D') : undefined,
              tone: 'good',
          }
        : { k: '센터 수락', v: '대기', s: undefined, tone: 'hot' };

    /*
     * 🔴 「담당 예정」이라는 칸은 없다 (대표 확정 ⓐ).
     *    시안은 미수락 강사에 「담당 예정」을 썼지만, 배정은 수락과 무관하게
     *    이미 되어 있는 사실이다. 같은 값에 이름을 둘로 두면 원장이 다른
     *    수라고 읽는다. 언제나 「담당 회원」이다.
     */
    const assigned: OnboardingCell = {
        k: '담당 회원',
        v: String(row.memberCount),
        unit: '명',
        tone: row.memberCount === 0 && row.isConnected ? 'hot' : 'plain',
    };

    const app: OnboardingCell = (() => {
        if (row.memberAppLinked == null) return { k: '회원 앱 다운', ...UNKNOWN };
        const missing = Math.max(0, row.memberCount - row.memberAppLinked);
        return {
            k: '회원 앱 다운',
            v: String(row.memberAppLinked),
            unit: `/${row.memberCount}명`,
            s: missing > 0 ? `다운 안 함 ${missing}` : '전원 다운',
            tone: missing > 0 ? 'warn' : 'good',
        };
    })();

    const pass: OnboardingCell = (() => {
        if (row.memberWithPass == null) return { k: '수강권', ...UNKNOWN };
        const missing = Math.max(0, row.memberCount - row.memberWithPass);
        return {
            k: '수강권',
            v: String(row.memberWithPass),
            unit: `/${row.memberCount}명`,
            s: missing > 0 ? `없음 ${missing}` : undefined,
            /*
             * 🔴 이 칸은 **세팅 관점**이라 만료자를 「있음」으로 센다 (QA6).
             *    라벨만 보면 「지금 쓸 수 있는 수강권」으로 읽히므로 밝혀 둔다 —
             *    안 밝히면 원장님이 만료 회원을 세팅 구멍으로 다시 센다.
             */
            title:
                '이 센터에서 한 번이라도 수강권을 등록한 회원 수입니다 · 만료된 회원도 포함합니다\n' +
                '만료는 세팅 구멍이 아니라 재등록 영업의 일이라, 여기서는 「있음」으로 셉니다',
            tone: missing > 0 ? (stage === 'PASS' ? 'hot' : 'warn') : 'good',
        };
    })();

    const week: OnboardingCell = (() => {
        if (locked) return lock('이번 주 수업');
        if (row.weekLessons == null) return { k: '이번 주 수업', ...UNKNOWN };
        const activePass = activePassOf(row);
        const noBooking = activePass == null ? null : shortfall(activePass, row.memberWithBooking);
        return {
            k: '이번 주 수업',
            v: String(row.weekLessons),
            unit: '건',
            s: noBooking ? `예약 없음 ${noBooking}명` : undefined,
            tone: stage === 'BOOKING' ? 'hot' : 'plain',
        };
    })();

    const journal: OnboardingCell = (() => {
        if (locked) return lock('일지 · 최근 7일');
        if (row.journal7d == null) return { k: '일지 · 최근 7일', ...UNKNOWN };
        const { written, total } = row.journal7d;
        const missing = Math.max(0, total - written);
        return {
            k: '일지 · 최근 7일',
            v: String(written),
            unit: `/${total}건`,
            s: missing > 0 ? `안 쓴 ${missing}건` : undefined,
            tone: missing > 0 ? (stage === 'JOURNAL' ? 'hot' : 'warn') : 'good',
        };
    })();

    return [accept, assigned, app, pass, week, journal];
};

/* ── 카드 한 장이 들고 다니는 것 ──────────────────────────── */

export type CoachCardView = {
    row: CoachOnboardingRow;
    stage: CoachStage;
    /** 「오늘 챙길 일」에 세는가 — 순항과 오래된 미수락은 false */
    chore: boolean;
    /** 분기가 지나도록 미수락 — 화면엔 남지만 오늘 할 일은 아니다 ([[STALE_ACCEPT_DAYS]]) */
    stale: boolean;
    headline: string;
    unlockNote: string | null;
    badge: string | null;
    actor: ReturnType<typeof actorOf>;
    waitingDays: number | null;
    tier: EscalationTier;
    cells: OnboardingCell[];
    /** 어제 진전 — 서버가 줄 때만 (대표 확정 ⓑ) */
    progressNote: string | null;
};

export const toCardView = (row: CoachOnboardingRow, today: dayjs.Dayjs): CoachCardView => {
    const stage = stageOf(row);
    const waitingDays = waitingDaysOf(row, today);
    const tier = escalationOf(row, waitingDays);
    const cells = cellsOf(row, stage);
    /* 수락 대기 칸의 「n일째」는 경과일과 같은 값이어야 한다 — 여기서 한 번만 센다 */
    if (waitingDays != null) cells[0] = { ...cells[0], s: `${waitingDays}일째` };
    const stale = isStaleAccept(row, today);
    return {
        row,
        stage,
        stale,
        /*
         * 🔴 오래된 미수락(90일+)은 **오늘 챙길 일에서 뺀다** (QA38).
         *    `chore` 하나만 내리면 건수·정렬·팝업·앞줄 보충이 전부 따라온다 —
         *    이 값을 보는 곳이 그 넷이다. 각자 걸러 내게 두면 또 갈린다.
         */
        chore: stage !== 'CLEAR' && !stale,
        headline: headlineOf(row, stage, waitingDays, tier),
        unlockNote: unlockNoteOf(stage),
        badge: badgeOf(row, stage),
        actor: actorOf(stage),
        waitingDays,
        tier,
        cells,
        progressNote: row.progressNote ?? null,
    };
};

/**
 * 카드 정렬 — 퍼널 순 → 경과일 긴 순 → 순항은 맨 아래 (정책 §3-3).
 *
 * ⚠️ 서버가 이미 정렬해서 준다고 계약에 적혀 있지만 **화면도 정렬한다.**
 *    목록에 손을 대는 것(가리기·조용한 모드)은 화면이 하는 일이라,
 *    서버 순서를 그대로 믿으면 걸러 낸 뒤 순서가 깨진다.
 */
export const sortCards = (cards: CoachCardView[]): CoachCardView[] =>
    [...cards].sort((a, b) => {
        const ra = a.chore ? FUNNEL.indexOf(a.stage) : FUNNEL.length;
        const rb = b.chore ? FUNNEL.indexOf(b.stage) : FUNNEL.length;
        if (ra !== rb) return ra - rb;
        /* 같은 단계면 오래 기다린 쪽이 위 */
        const da = a.waitingDays ?? 0;
        const db = b.waitingDays ?? 0;
        if (da !== db) return db - da;
        return a.row.name.localeCompare(b.row.name, 'ko');
    });

/* ── 히어로 요약 ──────────────────────────────────────────── */

export type OnboardingSummary = {
    /** 정식 회원 — Σ memberCount (서버 검산 ①과 같은 수여야 한다) */
    members: number;
    opportunities: number;
    coaches: number;
    waitingAccept: number;
    noPass: number | null;
    noBooking: number | null;
    appMissing: number | null;
};

/**
 * 히어로 아래 요약 줄 — **카드 합계와 반드시 일치** (정책 §2).
 *
 * 🔴 못 잰 값이 하나라도 섞이면 그 항목은 **null 로 남긴다.** 아는 것만
 *    더해서 숫자를 만들면 「수강권 없음 4명」이라고 적어 놓고 카드에는
 *    6명이 있는 상태가 된다 — 그 순간 원장은 화면 전체를 못 믿는다.
 */
export const summarize = (rows: CoachOnboardingRow[]): OnboardingSummary => {
    const sumOrNull = (
        list: CoachOnboardingRow[],
        pick: (r: CoachOnboardingRow) => number | null
    ): number | null =>
        list.reduce<number | null>((acc, r) => {
            if (acc == null) return null;
            const v = pick(r);
            return v == null ? null : acc + v;
        }, 0);

    return {
        members: rows.reduce((s, r) => s + r.memberCount, 0),
        opportunities: rows.reduce((s, r) => s + r.opportunityCount, 0),
        coaches: rows.length,
        waitingAccept: rows.filter((r) => !r.isConnected).length,
        noPass: sumOrNull(rows, (r) => shortfall(r.memberCount, r.memberWithPass)),
        /*
         * 예약 없음 — **두 겹으로 좁힌다** (시안 검산표: 「수강권 보유·수락된
         * 강사 담당만 셈」).
         *
         *   ① 수강권 보유자 중에서만. 수강권이 없으면 예약 자체가 불가라
         *      (서버 검산 ②) 섞으면 같은 사람을 두 번 독촉한다.
         *   ② 🔴 **미수락 강사는 아예 뺀다.** 그 강사의 예약 칸은 「수락 후」로
         *      잠겨 있다 — 못 잰 것이 아니라 **아직 물어볼 수 없는 것**이다.
         *      null 로 전파시키면 수락 대기 강사가 한 명이라도 있는 센터는
         *      예약 요약이 영원히 안 뜬다(신규 센터는 대부분 그렇다).
         */
        noBooking: sumOrNull(
            rows.filter((r) => r.isConnected),
            (r) => {
                const active = activePassOf(r);
                return active == null ? null : shortfall(active, r.memberWithBooking);
            }
        ),
        appMissing: sumOrNull(rows, (r) => shortfall(r.memberCount, r.memberAppLinked)),
    };
};

/** 요약 줄 문장 — 못 잰 항목은 아예 빼고 적는다 */
export const summaryLine = (s: OnboardingSummary): string => {
    const parts = [
        `정식 회원 ${s.members}명`,
        s.opportunities > 0 ? `신규기회 ${s.opportunities}명` : null,
        `강사 ${s.coaches}명`,
        s.waitingAccept > 0 ? `수락 대기 ${s.waitingAccept}` : null,
        s.noPass != null && s.noPass > 0 ? `수강권 없음 ${s.noPass}` : null,
        s.noBooking != null && s.noBooking > 0 ? `예약 없음 ${s.noBooking}` : null,
        s.appMissing != null && s.appMissing > 0 ? `앱 다운 안 함 ${s.appMissing}` : null,
    ].filter(Boolean);
    return parts.join(' · ');
};
